'use client'

import React, {useCallback, useState} from 'react'
import {cn, focusClasses, transitionClasses} from '../../../lib/utils'
import {Reveal} from '@fitro/customer-ui/Reveal'
import {Button} from '@fitro/customer-ui/Button'
import {Typography} from '@fitro/customer-ui/Typography'

type AlertSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical'
type AlertSize = 'small' | 'medium' | 'large'

interface AlertAction {
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary' | 'ghost'
    loading?: boolean
}

interface AlertBannerProps {
    variant: AlertSeverity
    title?: string
    message: string | React.ReactNode
    dismissible?: boolean
    onClose?: () => void
    actions?: AlertAction[]
    icon?: React.ComponentType<{ className?: string }> | boolean
    size?: AlertSize
    className?: string
}

const alertVariants: Record<AlertSeverity, string> = {
    info: [
        'bg-primary-50 border-primary-200 text-primary-900',
        'dark:bg-primary-950/20 dark:border-primary-800/30 dark:text-primary-100'
    ].join(' '),

    success: [
        'bg-success-50 border-success-200 text-success-900',
        'dark:bg-success-950/20 dark:border-success-800/30 dark:text-success-100'
    ].join(' '),

    warning: [
        'bg-warning-50 border-warning-200 text-warning-900',
        'dark:bg-warning-950/20 dark:border-warning-800/30 dark:text-warning-100'
    ].join(' '),

    error: [
        'bg-error-50 border-error-200 text-error-900',
        'dark:bg-error-950/20 dark:border-error-800/30 dark:text-error-100'
    ].join(' '),

    critical: [
        'bg-error-100 border-error-300 text-error-950',
        'dark:bg-error-900/40 dark:border-error-700/50 dark:text-error-50',
        'shadow-lg shadow-error-200/50 dark:shadow-error-900/20'
    ].join(' ')
}

const iconVariants: Record<AlertSeverity, string> = {
    info: 'text-primary-500 dark:text-primary-400',
    success: 'text-success-500 dark:text-success-400',
    warning: 'text-warning-500 dark:text-warning-400',
    error: 'text-error-500 dark:text-error-400',
    critical: 'text-error-600 dark:text-error-400'
}

const sizeVariants: Record<AlertSize, string> = {
    small: 'p-3 sm:p-3 gap-2 text-body-small',
    medium: 'p-4 sm:p-4 gap-3 text-body',
    large: 'p-5 sm:p-6 gap-4 text-body-large'
}

const InfoIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
)

const SuccessIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
)

const WarningIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
    </svg>
)

const ErrorIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
)

const CriticalIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
)

const CloseIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
    </svg>
)

const defaultIcons: Record<AlertSeverity, React.ComponentType<{ className?: string }>> = {
    info: InfoIcon,
    success: SuccessIcon,
    warning: WarningIcon,
    error: ErrorIcon,
    critical: CriticalIcon
}

const AlertBanner = React.memo<AlertBannerProps>(({
                                                             variant,
                                                             title,
                                                             message,
                                                             dismissible = true,
                                                             onClose,
                                                             actions = [],
                                                             icon,
                                                             size = 'medium',
                                                             className
                                                         }) => {
    const [isVisible, setIsVisible] = useState(true)

    const handleClose = useCallback(() => {
        setIsVisible(false)
        onClose?.()
    }, [onClose])

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleClose()
        }
    }

    const IconComponent = icon === false ? null :
        (typeof icon === 'function' ? icon : defaultIcons[variant])

    const baseClasses = [
        'border rounded-lg',
        transitionClasses,
        'max-w-full overflow-hidden'
    ].join(' ')

    const classes = cn(
        baseClasses,
        alertVariants[variant],
        sizeVariants[size],
        'relative w-full',
        className
    )

    const banner = (
        <div
            className={classes}
            role="alert"
            aria-live={variant === 'critical' ? 'assertive' : 'polite'}
            aria-labelledby={title ? 'alert-title' : undefined}
            aria-describedby="alert-message"
        >
            <div className="flex items-start gap-3">

                {IconComponent && (
                    <div className="shrink-0 mt-0.5">
                        <IconComponent className={cn('w-5 h-5', iconVariants[variant])}/>
                    </div>
                )}


                <div className="flex-1 min-w-0">
                    {title && (
                        <Typography
                            variant="body"
                            className="font-semibold mb-1"
                            id="alert-title"
                        >
                            {title}
                        </Typography>
                    )}

                    <div id="alert-message" className="break-words">
                        {typeof message === 'string' ? (
                            <Typography variant={size === 'small' ? 'body-small' : 'body'}>
                                {message}
                            </Typography>
                        ) : (
                            message
                        )}
                    </div>


                    {actions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {actions.map((action, index) => (
                                <Button
                                    key={index}
                                    variant={action.variant || 'secondary'}
                                    size="small"
                                    loading={action.loading}
                                    onClick={action.onClick}
                                    className="min-h-[36px]">
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>


                {dismissible && (
                    <div className="shrink-0 ml-auto">
                        <button
                            type="button"
                            onClick={handleClose}
                            onKeyDown={handleKeyDown}
                            className={cn(
                                'inline-flex items-center justify-center',
                                'w-11 h-11 rounded-md', 'text-current opacity-60 hover:opacity-100',
                                'transition-opacity duration-200',
                                focusClasses,
                                'touch-manipulation')}
                            aria-label="Zamknij alert"
                        >
                            <CloseIcon className="w-4 h-4"/>
                        </button>
                    </div>
                )}
            </div>
        </div>
    )

    // Reveal owns the entrance and exit, expanding in place instead of pushing the content below in one frame.
    return <Reveal show={isVisible} appear>{banner}</Reveal>
})

AlertBanner.displayName = 'AlertBanner'

export {AlertBanner}
export type {AlertBannerProps}
